import { QueryService } from '@captiveaire/query';
import { ToastService } from '@captiveaire/toast';
import { Menu } from '@caslink/common-client';
import { DeviceStatusService } from './device-status.service';
import { IChangeCommunicationModuleRequest } from '../models/change-communication-module-request';
import { IDetailViewFilter } from '../models/detail-view-filter.interface';
import { DeviceLocationDetail } from '../models/device-location-detail.interface';
import { DeviceLocationDeviceDetail } from '../models/device-location-device-detail.interface';
import { DeviceLocationModuleDetail } from '../models/device-location-module-detail.interface';
import { IDeviceStatusListSearchCriteria } from '../models/device-status-list-search-criteria.interface';
import { Injectable } from '@angular/core';

export interface IDraggedDevice {
    device: DeviceLocationDeviceDetail;
    element: HTMLElement;
}

export interface IDeviceDropTarget {
    module: DeviceLocationModuleDetail;
    targetElement: HTMLElement;
    isFiltered: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class DetailViewService {
    public data: DeviceLocationDetail;
    public movedDevice: IDraggedDevice;
    public shadowElement: HTMLElement;

    public _moduleMenuItems: Menu;
    public showModuleHamburger: boolean;
    public getData: () => Promise<any>;
    public query: IDeviceStatusListSearchCriteria;

    public filter: IDetailViewFilter;
    private highlighted: HTMLElement;

    constructor(
        private deviceStatusService: DeviceStatusService,
        private queryService: QueryService,
        private toastService: ToastService
    ) { }

    public init(
        locationId: number,
        moduleId: number,
        jobNumber: number,
        query: IDeviceStatusListSearchCriteria
    ) {
        this.filter = this.getEmptyFilter();

        if (query == null) {
            const defaultLimit = 10;
            this.query = this.queryService.createQuery(defaultLimit, 'Id', 1, true) as IDeviceStatusListSearchCriteria;

            this.query.locationId = locationId;
            this.query.moduleId = moduleId;
            this.query.jobNumber = jobNumber;
        } else {
            this.query = query;
            this.filter.category = this.query.hardwareCategory;
            this.filter.categoryId = this.query.hardwareCategoryId;
            this.filter.wildcard = this.query.wildcard;
        }
    }

    get menuItems() {
        return this._moduleMenuItems ? this._moduleMenuItems.items as any : [];
    }

    set menuItems(value: Menu) {
        this._moduleMenuItems = value;
        this.showModuleHamburger = this.menuItems && Object.keys(value).length > 0;
    }

    public dragStart(event: DragEvent, device?: DeviceLocationDeviceDetail) {
        this.movedDevice = {
            device: device,
            element: (event.target as Element).parentElement as HTMLElement
        };

        this.movedDevice.element.classList.add('moved-device');

        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.dropEffect = 'move';
    }

    public dragOver(event: DragEvent, module: DeviceLocationModuleDetail, isFiltered?: boolean, device?: DeviceLocationDeviceDetail) {
        if (!this.movedDevice || !this.movedDevice.device || !(event.target instanceof Element)) {
            return;
        }

        const targetElement = event.target as Element;

        const dropTarget = this.getDropTarget(targetElement, module, isFiltered);

        if (dropTarget.module.moduleId !== this.movedDevice.device.moduleId) {
            this.removeDropHighlight();
            this.highlighted = dropTarget.targetElement;
            this.highlighted.classList.add('card-hover');
            this.addShadowElement(dropTarget, event, device);
            event.preventDefault();
        }
    }

    public drop(event: DragEvent, module: DeviceLocationModuleDetail, isFiltered: boolean) {
        event.preventDefault();
        const targetElement = event.target as Element;

        const dropTarget = this.getDropTarget(targetElement, module, isFiltered);
        const previousMac = this.movedDevice.device.macAddress;
        this.movedDevice.device.macAddress = dropTarget.module.macAddress;

        this.deviceStatusService.promptCommModuleChange(
            {
                category: this.movedDevice.device.category,
                hardwareItemId: this.movedDevice.device.hardwareItem.id,
                newMacAddress: this.movedDevice.device.macAddress,
                previousMacAddress: previousMac,
                sequence: this.movedDevice.device.sequence
            } as IChangeCommunicationModuleRequest,
            (success: boolean) => {
                this.onAfterMove(success);
            });
        this.dragEnd();
    }

    public dragEnd(event?: DragEvent) {
        if (this.movedDevice != null) {
            this.movedDevice.element.classList.remove('moved-device');
            this.movedDevice = null;
        }
        this.removeDropHighlight();
        this.removeShadowElement();
    }

    public addShadowElement(dropTarget: IDeviceDropTarget, event: DragEvent, device?: DeviceLocationDeviceDetail) {
        if (this.shadowElement == null) {
            const card = document.createElement('mat-card');
            card.classList.add('control-listing');
            card.classList.add('shadow-device');

            const div = document.createElement('div');
            div.classList.add('card-information');
            div.innerText = this.movedDevice.device.tag;

            card.appendChild(div);
            this.shadowElement = card;
        }

        if (this.shadowElement && device == null) {
            return;
        }

        if (device) {
            dropTarget.targetElement.children[1].insertBefore(
                this.shadowElement, dropTarget.targetElement.children[1].children[device.$index + 1]
            );
        } else if (!dropTarget.module.devices || dropTarget.module.devices.length === 0 || !dropTarget.isFiltered) {
            (dropTarget.targetElement.children[1] as any).append(this.shadowElement);
        }
    }

    public leave(event: DragEvent) {
        const targetEle = event.target as Element;
        const dropTarget = this.getDropTarget(targetEle, null, false);

        if (this.isElementValidLeave(event, dropTarget.targetElement)) {
            this.removeDropHighlight();
            this.removeShadowElement();
        }
    }

    public isElementValidLeave(event: any /* DragEvent? */, targetElement: HTMLElement): boolean {
        if (event.fromElement && event.fromElement.nodeName === 'DIV' && event.fromElement.classList.contains('card-information')) {
            return false;
        } else if (event.srcElement === targetElement) {
            return true;
        } else
            // if leaving title or device container
            if (targetElement.children[0] === event.srcElement || targetElement.children[1] === event.srcElement) {
                return true;
            }
        return false;
    }

    public removeShadowElement() {
        if (this.shadowElement) {
            this.shadowElement.remove();
            this.shadowElement = null;
        }
    }

    public resetFilter() {
        this.query.locationId = null;
        this.query.jobNumber = null;
        this.query.moduleId = null;

        this.filter = this.getEmptyFilter();
    }

    private getEmptyFilter(): IDetailViewFilter {
        return {
            category: '',
            categoryId: null,
            wildcard: ''
        } as IDetailViewFilter;
    }

    private getDropTarget(targetElement: Element, module: DeviceLocationModuleDetail, isFiltered: boolean): IDeviceDropTarget {
        const cardEle = targetElement.closest('mat-card[clDropElement]');

        return {
            isFiltered: !!isFiltered,
            module: module,
            targetElement: cardEle as HTMLElement
        } as IDeviceDropTarget;
    }

    private removeDropHighlight() {
        if (this.highlighted) {
            this.highlighted.classList.remove('card-hover');
        }
    }

    private onAfterMove(success: boolean) {
        const message = success ? 'Device Moved' : 'Move Failed!';
        this.toastService.showSimple(message);
        this.getData();
    }
}
